import { Trans, useLingui } from "@lingui/react/macro";
import { patientKeys, updateTeam, useTeams } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type { ITeam } from "@medoma/models/patients";
import type { SiteId } from "@medoma/models/sites";
import Select from "@medoma/components/Select/Select";

export const EditTeam = ({
  currentTeam,
  onSuccess,
  patientId,
  siteId,
}: {
  currentTeam: ITeam | undefined | null;
  onSuccess: () => void;
  patientId: string;
  siteId: SiteId;
}) => {
  const { t } = useLingui();
  const currentTeamIdAsString = currentTeam ? currentTeam.id.toString() : null;
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      teamId: currentTeamIdAsString,
    },
  });
  const queryClient = useQueryClient();
  const {
    data: teams,
    isPending: isPendingTeams,
    isError: isErrorTeams,
  } = useTeams(siteId);
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (team: number | null) => updateTeam({ patientId, team }),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        if (formData.teamId) {
          mutate(Number(formData.teamId));
        } else {
          mutate(null);
        }
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar team`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            {isPendingTeams ? (
              <Loading message={t`Hämtar team`} />
            ) : isErrorTeams ? (
              <ErrorMessage message={t`Kunde inte hämta team`} />
            ) : (
              <Select
                label={t`Team`}
                hideLabel
                {...register("teamId")}
                errorMessage={errors.teamId?.message}
              >
                <option value={""}>{t`Inget`}</option>
                {teams.map((team) => (
                  <option value={team.id.toString()} key={team.id}>
                    {team.displayName}
                  </option>
                ))}
              </Select>
            )}
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : (
            <></>
          )}
        </>
      )}
    </Form>
  );
};
